import { HostDetail, HostInput, HostMetrics, OverviewHost, PublicHostEntry, WarehousePayload } from './types';

class ApiError extends Error {
  status: number;
  constructor(status: number, message: string) {
    super(message);
    this.status = status;
  }
}

async function request<T>(path: string, init?: RequestInit): Promise<T> {
  const response = await fetch(path, {
    credentials: 'same-origin',
    headers: init?.body ? { 'Content-Type': 'application/json' } : undefined,
    ...init,
  });
  if (response.status === 401) {
    // Session missing or expired: send the user through Google sign-in.
    window.location.href = '/auth/google';
    throw new ApiError(401, 'Authentication required');
  }
  const body = (await response.json().catch(() => ({}))) as Record<string, unknown>;
  if (!response.ok) {
    throw new ApiError(response.status, typeof body.error === 'string' ? body.error : `HTTP ${response.status}`);
  }
  return body as T;
}

export const api = {
  me: () => request<{ user: { email: string; name?: string } }>('/api/me'),
  logout: () => request<{ message: string }>('/auth/logout', { method: 'POST' }),
  overview: () => request<{ hosts: OverviewHost[] }>('/api/overview'),
  hostDetail: (name: string) => request<HostDetail>(`/api/hosts/${encodeURIComponent(name)}`),
  hosts: () => request<{ hosts: PublicHostEntry[] }>('/api/hosts'),
  addHost: (input: HostInput) =>
    request<{ host: PublicHostEntry }>('/api/hosts', { method: 'POST', body: JSON.stringify(input) }),
  updateHost: (name: string, input: HostInput) =>
    request<{ host: PublicHostEntry }>(`/api/hosts/${encodeURIComponent(name)}`, {
      method: 'PUT',
      body: JSON.stringify(input),
    }),
  removeHost: (name: string) =>
    request<{ message: string }>(`/api/hosts/${encodeURIComponent(name)}`, { method: 'DELETE' }),
  metrics: () => request<{ hosts: HostMetrics[] }>('/api/metrics'),
  saveSiteConfiguration: (hostName: string, siteName: string, configuration: Record<string, unknown>) =>
    request<{ site_name: string; configuration: Record<string, unknown> }>(
      `/api/hosts/${encodeURIComponent(hostName)}/sites/${encodeURIComponent(siteName)}/configuration`,
      { method: 'PUT', body: JSON.stringify({ configuration }) }
    ),
  siteConfiguration: (hostName: string, siteName: string) =>
    request<{ site_name: string; configuration: Record<string, unknown> }>(
      `/api/hosts/${encodeURIComponent(hostName)}/sites/${encodeURIComponent(siteName)}/configuration`
    ),
  siteWarehouse: (hostName: string, siteName: string) =>
    request<{ site_name: string; warehouse: WarehousePayload }>(
      `/api/hosts/${encodeURIComponent(hostName)}/sites/${encodeURIComponent(siteName)}/warehouse`
    ),
  saveSiteWarehouse: (hostName: string, siteName: string, warehouse: WarehousePayload) =>
    request<{ site_name: string; warehouse: WarehousePayload }>(
      `/api/hosts/${encodeURIComponent(hostName)}/sites/${encodeURIComponent(siteName)}/warehouse`,
      { method: 'POST', body: JSON.stringify({ warehouse }) }
    ),
};
